import React, { useMemo } from 'react';
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '@lib/utils';
import { useAuth } from '@hooks/useAuth';
import { useUnreadCountValue } from '@hooks/useUnreadCount';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';

const createImageIcon = (src: string): React.FC<{ className?: string }> => {
  const IconImage: React.FC<{ className?: string }> = ({ className }) => (
    <img src={src} className={cn('h-6 w-6 select-none', className)} alt="" aria-hidden="true" />
  );
  IconImage.displayName = 'BottomNavIcon';
  return IconImage;
};

const baseUrl = import.meta.env.BASE_URL;
const EventsIcon = createImageIcon(`${baseUrl}events-bottom-nav-icon.svg`);
const HomeIcon = createImageIcon(`${baseUrl}home-bottom-nav-icon.svg`);
const ConnectIcon = createImageIcon(`${baseUrl}assets/QR_icon.svg`);
const ChatIcon = createImageIcon(`${baseUrl}chat-bottom-nav-icon.svg`);

interface NavItem {
  id: 'events' | 'home' | 'connect' | 'chat';
  label: string;
  path?: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
  isCenter?: boolean;
  disabled?: boolean;
  matchPaths?: string[];
  requiresAuth?: boolean;
}

/**
 * Bottom navigation component with tab navigation
 * Layout: Events - Home (center) - Connect - Messages
 *
 * Reads `showAuthenticatedUI` from `useAuth()` to gate badge + auth-required
 * nav UI. Unread polling is owned by BadgeProvider (mounted at RootLayout);
 * this component just subscribes to the resulting cache via
 * `useUnreadCountValue` — no second polling timer.
 */
export interface BottomNavigationProps {
  /** Path to user's profile for Connect button (includes slug) */
  connectPath?: string;
  /** Callback when user clicks a nav item that requires authentication (guests only) */
  onProtectedNavClick?: (action: string) => void;
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  connectPath,
  onProtectedNavClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { showAuthenticatedUI } = useAuth();
  const { trackEvent } = useAmplitudeTracking();
  const { data: unreadCountData } = useUnreadCountValue();
  const unreadMessages = unreadCountData?.total_unread_chats ?? 0;

  // Navigation items configuration - ordered left to right
  const navItems: NavItem[] = useMemo(
    () => [
      {
        id: 'events',
        label: 'Spaces',
        icon: EventsIcon,
        path: '/events',
        requiresAuth: false,
      },
      {
        id: 'home',
        label: 'Home',
        icon: HomeIcon,
        path: '/home',
        isCenter: true,
        requiresAuth: false,
      },
      {
        id: 'connect',
        label: 'Connect',
        icon: ConnectIcon,
        path: connectPath ?? '/profile', // Use placeholder path for guests
        matchPaths: connectPath ? [`${connectPath}/edit`] : undefined,
        requiresAuth: true,
      },
      {
        id: 'chat',
        label: 'messages',
        icon: ChatIcon,
        path: '/messages',
        badge: showAuthenticatedUI ? unreadMessages : undefined,
        matchPaths: ['/chat'],
        requiresAuth: true,
      },
    ],
    [connectPath, unreadMessages, showAuthenticatedUI],
  );

  // Action descriptions for login prompt
  const actionDescriptions: Record<string, string> = {
    connect: 'connect with others',
    chat: 'access messages',
  };

  const handleNavigation = (item: NavItem) => {
    trackEvent(ANALYTICS_EVENTS.cta_clicked, {
      cta_name: `nav_${item.id}`,
      source_context: location.pathname,
    });

    // If item requires auth and user is not authenticated, delegate to parent
    if (item.requiresAuth && !showAuthenticatedUI) {
      const action = actionDescriptions[item.id] || item.label.toLowerCase();
      onProtectedNavClick?.(action);
      return;
    }

    if (!item.path) return;

    // Special handling for Connect button - show QR dialog
    if (item.id === 'connect' && item.path) {
      const showQrSearch = `?${createSearchParams({ show_qr: '1' }).toString()}`;
      navigate({ pathname: item.path, search: showQrSearch });
      return;
    }

    navigate(item.path);
  };

  return (
    <nav className="flex h-[60px] w-full items-center justify-center px-3">
      <div className="flex h-full w-full items-center justify-around gap-1 rounded-[32px] bg-white/60 px-3 py-2 shadow-float backdrop-blur-[6px] backdrop-brightness-100 [-webkit-backdrop-filter:blur(6px)_brightness(100%)]">
        {navItems.map(item => {
          const Icon = item.icon;
          const badgeCount = Number(item.badge) || 0;
          const matchesExtraPath = item.matchPaths?.some(path =>
            location.pathname.startsWith(path),
          );
          const isActive = Boolean(
            (item.path &&
              (location.pathname === item.path ||
                (item.path !== '/home' && location.pathname.startsWith(item.path)))) ||
            matchesExtraPath,
          );
          const activeButtonClass =
            'bg-mb-nav-active text-black hover:bg-mb-nav-active hover:text-black';
          const inactiveButtonClass = 'text-gray-600 hover:text-black';

          return (
            <div
              key={item.id}
              className="relative flex h-full flex-1 flex-col items-center justify-center"
            >
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  handleNavigation(item);
                }}
                aria-label={item.label}
                className={cn(
                  'bottom-nav-button relative flex h-[32px] w-8 items-center justify-center rounded-full p-0 transition-all duration-200 max-h-[32px] focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-1 focus-visible:outline-hidden',
                  isActive ? activeButtonClass : inactiveButtonClass,
                )}
              >
                <span className="relative flex w-full items-center justify-center">
                  <Icon className={item.isCenter ? 'h-6 w-6' : 'h-5 w-5'} />
                  {badgeCount > 0 && showAuthenticatedUI && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 z-20 flex h-4 w-4 items-center justify-center rounded-full p-0 text-[9px] font-semibold"
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </Badge>
                  )}
                </span>
              </Button>
              <span
                className={cn(
                  // The tab's label sits outside its Button, so it takes the long-press rule itself.
                  'mt-1 select-none text-[10px] font-medium uppercase leading-none tracking-[0.02em]',
                  isActive ? 'text-black' : 'text-gray-600',
                )}
              >
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    </nav>
  );
};
